import { useCallback, useEffect, useState } from 'react';
import { CompetitionPermissions, errorStatus, fetchMyPermissions } from '../../services/manageApi';

export interface PermissionsState {
  permissions: CompetitionPermissions | null;
  loading: boolean;
  /** HTTP status when loading failed (403 = not a host), 0 for network errors, null on success. */
  failureStatus: number | null;
  canEdit: (field: string) => boolean;
  can: (action: string) => boolean;
}

export function usePermissions(competitionId: number): PermissionsState {
  const [permissions, setPermissions] = useState<CompetitionPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [failureStatus, setFailureStatus] = useState<number | null>(null);

  useEffect(() => {
    if (!Number.isFinite(competitionId)) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchMyPermissions(competitionId)
      .then((result) => {
        if (cancelled) return;
        setPermissions(result);
        setFailureStatus(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setPermissions(null);
        setFailureStatus(errorStatus(err) ?? 0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [competitionId]);

  const canEdit = useCallback((field: string) => Boolean(permissions?.editable_fields.includes(field)), [permissions]);
  const can = useCallback((action: string) => Boolean(permissions?.actions.includes(action)), [permissions]);

  return { permissions, loading, failureStatus, canEdit, can };
}
